import { BoxType, ShipmentDataType } from '../types/shipment';
import { formatPendingRequirementLabel } from './fieldLabels';
import { validateCpscCompliance } from './usCompliance';
import { hasAdminAccess } from '../services/hasAdminAccess';

export interface PendingRequirementItem {
  label: string;
  boxIndex?: number;
}

export interface GetPendingRequirementsParams {
  boxes: BoxType[];
  shipmentType: ShipmentDataType;
  extraDetails?: any;
  consignee?: any;
  isShipmentExport: boolean;
  complianceRequired: boolean;
  euComplianceRule: { isRequired: boolean; [key: string]: any };
  isUSConsignee: boolean;
  isHSNValidating?: boolean;
  isProductFormInvalid?: boolean;
}

export const getPendingRequirements = (params: GetPendingRequirementsParams): PendingRequirementItem[] => {
  const {
    boxes,
    shipmentType,
    extraDetails,
    consignee,
    isShipmentExport,
    complianceRequired,
    euComplianceRule,
    isUSConsignee,
    isHSNValidating,
    isProductFormInvalid,
  } = params;

  const list: PendingRequirementItem[] = [];

  if (!boxes || boxes.length === 0) {
    list.push({ label: 'Boxes (At least 1 box is required)' });
    return list;
  }

  if (isHSNValidating) {
    list.push({ label: 'HSN code is currently verifying' });
  }

  if (isProductFormInvalid) {
    list.push({ label: 'Complete active product entry or cancel edit' });
  }

  boxes.forEach((box, idx) => {
    const boxNum = idx + 1;
    const weight = Number((box.weight && `${box.weight}`) || 0);
    const length = Number((box.length && `${box.length}`) || 0);
    const width = Number((box.width && `${box.width}`) || 0);
    const height = Number((box.height && `${box.height}`) || 0);

    if (!weight || weight <= 0) {
      list.push({ label: formatPendingRequirementLabel('weight', { boxNumber: boxNum }), boxIndex: idx });
    }
    if (!length || length <= 0) {
      list.push({ label: formatPendingRequirementLabel('length', { boxNumber: boxNum }), boxIndex: idx });
    }
    if (!width || width <= 0) {
      list.push({ label: formatPendingRequirementLabel('width', { boxNumber: boxNum }), boxIndex: idx });
    }
    if (!height || height <= 0) {
      list.push({ label: formatPendingRequirementLabel('height', { boxNumber: boxNum }), boxIndex: idx });
    }

    if (!box.products || box.products.length === 0) {
      list.push({
        label: `Box ${boxNum} — Declared Goods (At least 1 declared item is required)`,
        boxIndex: idx,
      });
    } else {
      box.products.forEach((p: any, pIdx: number) => {
        const rowNum = pIdx + 1;
        if (!p.description?.trim()) {
          list.push({
            label: formatPendingRequirementLabel('description', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }
        if (!p.quantity || Number(p.quantity) <= 0) {
          list.push({
            label: formatPendingRequirementLabel('quantity', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }
        if (p.unitValue === undefined || p.unitValue === null || Number(p.unitValue) <= 0) {
          list.push({
            label: formatPendingRequirementLabel('unitValue', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }
        if (isShipmentExport && !p.HSNCode?.trim()) {
          list.push({
            label: formatPendingRequirementLabel('HSNCode', { boxNumber: boxNum, productRowNumber: rowNum }),
            boxIndex: idx,
          });
        }

        if (complianceRequired) {
          if (!p.productId?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('productId', { boxNumber: boxNum, productRowNumber: rowNum }),
              boxIndex: idx,
            });
          }
          if (!p.productIdType?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('productIdType', { boxNumber: boxNum, productRowNumber: rowNum }),
              boxIndex: idx,
            });
          }
        }

        if (complianceRequired && euComplianceRule.isRequired) {
          if (!p.merchantProductId?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('merchantProductId', {
                boxNumber: boxNum,
                productRowNumber: rowNum,
              }),
              boxIndex: idx,
            });
          }
          if (!p.nonStandardManufacturerProductId?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('nonStandardManufacturerProductId', {
                boxNumber: boxNum,
                productRowNumber: rowNum,
              }),
              boxIndex: idx,
            });
          }
        }

        if (complianceRequired && isUSConsignee) {
          const cpscVal = validateCpscCompliance(p.cpscCompliance, true, true, hasAdminAccess());
          if (!cpscVal.isValid) {
            Object.keys(cpscVal.errors).forEach(errKey => {
              list.push({
                label: formatPendingRequirementLabel(errKey, {
                  boxNumber: boxNum,
                  productRowNumber: rowNum,
                }),
                boxIndex: idx,
              });
            });
          }
        }
      });
    }
  });

  // Shipment Mode-driven requirements
  if (!shipmentType?.shipmentMode?.trim()) {
    list.push({ label: formatPendingRequirementLabel('shipmentMode') });
  }

  const isB2BMode = (shipmentType?.shipmentMode || '').toUpperCase() === 'B2B';
  const vatIdVal = (consignee?.vat || shipmentType?.recipientVatTaxId || extraDetails?.recipientVatTaxId || '').trim();
  if (isB2BMode && !vatIdVal) {
    list.push({ label: formatPendingRequirementLabel('recipientVatTaxId') });
  }

  const isEcomMode = (shipmentType?.shipmentMode || '').toUpperCase() === 'ECOM';
  const purposeVal = (extraDetails?.purposeOfShipment || '').trim();
  if (isEcomMode && !purposeVal) {
    list.push({ label: formatPendingRequirementLabel('purposeOfShipment') });
  }

  // Incoterm-driven requirements
  const incotermVal = (shipmentType?.incoterm || extraDetails?.incoterm || '').trim().toUpperCase();
  if (!incotermVal) {
    list.push({ label: formatPendingRequirementLabel('incoterm') });
  } else if (incotermVal === 'CIF') {
    const frVal =
      shipmentType?.freight !== undefined && shipmentType?.freight !== ''
        ? shipmentType.freight
        : extraDetails?.freight;
    const insVal =
      shipmentType?.freightInsurance !== undefined && shipmentType?.freightInsurance !== ''
        ? shipmentType.freightInsurance
        : extraDetails?.freightInsurance;
    if (!frVal || Number(frVal) <= 0) {
      list.push({ label: formatPendingRequirementLabel('freight') });
    }
    if (!insVal || Number(insVal) <= 0) {
      list.push({ label: formatPendingRequirementLabel('freightInsurance') });
    }
  } else if (incotermVal === 'CNF') {
    const frVal =
      shipmentType?.freight !== undefined && shipmentType?.freight !== ''
        ? shipmentType.freight
        : extraDetails?.freight;
    if (!frVal || Number(frVal) <= 0) {
      list.push({ label: formatPendingRequirementLabel('freight') });
    }
  }

  return list;
};
